// Weather lookup chain: UK postcode → lat/lon (postcodes.io) → current
// temperature in °C (open-meteo). Both free, no API keys.

export interface PostcodeLocation {
  postcode: string;
  latitude: number;
  longitude: number;
  region: string | null;
}

export interface CurrentWeather {
  tempC: number;
  asOf: Date;
}

const POSTCODES_API = "https://api.postcodes.io/postcodes/";
const OPEN_METEO_API = "https://api.open-meteo.com/v1/forecast";

function normalisePostcode(raw: string): string {
  return raw.replace(/\s+/g, "").toUpperCase();
}

export async function lookupPostcode(postcode: string): Promise<PostcodeLocation | null> {
  const code = normalisePostcode(postcode);
  if (!code) return null;
  const res = await fetch(`${POSTCODES_API}${encodeURIComponent(code)}`);
  if (!res.ok) return null;
  const data = (await res.json()) as {
    result?: {
      postcode?: string;
      latitude?: number;
      longitude?: number;
      region?: string | null;
    };
  };
  const r = data.result;
  if (!r || typeof r.latitude !== "number" || typeof r.longitude !== "number") {
    return null;
  }
  return {
    postcode: r.postcode ?? code,
    latitude: r.latitude,
    longitude: r.longitude,
    region: r.region ?? null,
  };
}

export async function fetchCurrentTemp(latitude: number, longitude: number): Promise<CurrentWeather | null> {
  const url = `${OPEN_METEO_API}?latitude=${latitude}&longitude=${longitude}&current=temperature_2m&timezone=auto`;
  const res = await fetch(url);
  if (!res.ok) return null;
  const data = (await res.json()) as {
    current?: { temperature_2m?: number; time?: string };
  };
  const tempC = data.current?.temperature_2m;
  if (typeof tempC !== "number") return null;
  return { tempC, asOf: data.current?.time ? new Date(data.current.time) : new Date() };
}

export async function getCurrentTempByPostcode(postcode: string): Promise<CurrentWeather | null> {
  const loc = await lookupPostcode(postcode);
  if (!loc) return null;
  return fetchCurrentTemp(loc.latitude, loc.longitude);
}
